import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/** `stick`: a joystick wherever the thumb touches (see bindInput). */
export type ControlScheme = 'swipe' | 'stick' | 'dpad' | 'both';
export type Quality = 'auto' | 'high' | 'low';
export type HapticLevel = 'off' | 'soft' | 'strong';
export type SwipeSensitivity = 'low' | 'normal' | 'high';
/** How closely your snake's drawn head follows its turns: smooth rounds corners more, snappy shows them sooner. */
export type TurnFeel = 'smooth' | 'snappy';

interface SettingsState {
  /** Master switch for all audio. */
  sound: boolean;
  music: boolean;
  /** 0..1 */
  musicVolume: number;
  sfxVolume: number;
  haptics: HapticLevel;
  controls: ControlScheme;
  /** D-pad and boost button on the left side of the screen. */
  leftHanded: boolean;
  /** How far a finger travels before a swipe turns: low ignores small wobbles, high turns on the shortest flick. */
  swipeSensitivity: SwipeSensitivity;
  turnFeel: TurnFeel;
  quality: Quality;
  reduceMotion: boolean;
  showNetStats: boolean;
  voiceAutoJoin: boolean;
  /** The first-match "swipe to turn" hint has done its job (the player turned once). */
  seenSwipeHint: boolean;
  seenPortalNotice: boolean;
  /** Played (or skipped) the guided first match. */
  tutorialDone: boolean;
  /** Closed the "Get the app" card on Home (it stays in Settings). */
  dismissedGetApp: boolean;
  /** Cosmetics are mirrored here so guests and offline practice use them too. */
  skin: string;
  theme: string;
  /** The player's own background, kept while another theme is in use. */
  customTheme: string;
  set: (patch: Partial<Omit<SettingsState, 'set'>>) => void;
}

export const useSettings = create<SettingsState>()(
  persist(
    (set) => ({
      sound: true,
      music: true,
      musicVolume: 0.6,
      sfxVolume: 0.8,
      haptics: 'strong',
      controls: 'swipe',
      leftHanded: false,
      swipeSensitivity: 'normal',
      turnFeel: 'smooth',
      quality: 'auto',
      reduceMotion: false,
      showNetStats: false,
      voiceAutoJoin: false,
      seenSwipeHint: false,
      seenPortalNotice: false,
      tutorialDone: false,
      dismissedGetApp: false,
      skin: 'emerald',
      theme: 'midnight',
      customTheme: '',
      set: (patch) => set(patch),
    }),
    {
      name: 'snakefeast.settings',
      version: 3,
      // v2: swipe became the default. 'both' was the old default, so treat it as never chosen.
      // v3: vibration went from on/off to off/soft/strong.
      migrate: (state, version) => {
        const s = state as Partial<SettingsState>;
        if (version < 2 && s.controls === 'both') s.controls = 'swipe';
        if (version < 3) s.haptics = (s.haptics as unknown) === false ? 'off' : 'strong';
        return s as SettingsState;
      },
    },
  ),
);
